import { DateTime } from 'luxon'
import { Employee, employeeFromDto, EmployeeID, EmployeeJson } from '../../employees/models/employee'
import { Shift, ShiftDto, shiftFromDto, ShiftID } from '../../shifts/models/shift'
import z from 'zod'

type EmployeeRelation = {
    data: EmployeeJson
} | null

type ShiftRelation = {
    data: ShiftDto
} | null

export const ShiftOfferStatus = z.enum(['pending', 'accepted', 'applied', 'rejected', 'declined', 'queued'])
export type ShiftOfferStatus = z.infer<typeof ShiftOfferStatus>

export interface EmployeeShiftOfferDto {
    employee_id: string
    shift_id: string

    status: ShiftOfferStatus

    checked_in_at: string | null
    check_in_lat: string | null
    check_in_lng: string | null
    check_in_note: string | null

    checked_out_at: string | null
    check_out_lat: string | null
    check_out_lng: string | null
    check_out_note: string | null

    queue_order?: number | null
    queue_process_on?: string | null

    employee?: EmployeeRelation
    shift?: ShiftRelation
}

export interface EmployeeShiftOffer {
    employeeId: EmployeeID
    shiftId: ShiftID

    status: ShiftOfferStatus

    checkedInAt: DateTime | null
    checkInLat: string | null
    checkInLng: string | null
    checkInNote: string | null

    checkedOutAt: DateTime | null
    checkOutLat: string | null
    checkOutLng: string | null
    checkOutNote: string | null

    queueOrder?: number
    queueProcessOn?: DateTime

    duration: Duration | null

    employee: Employee | null
    shift: Shift | null
}

export const createEmployeeShiftOffer = (
    params: {
        employeeId: EmployeeID
        shiftId: ShiftID
        status: ShiftOfferStatus
        checkedInAt?: DateTime | null
        checkInLat?: string | null
        checkInLng?: string | null
        checkInNote?: string | null
        checkedOutAt?: DateTime | null
        checkOutLat?: string | null
        checkOutLng?: string | null
        checkOutNote?: string | null
        queueOrder?: number | null
        queueProcessOn?: DateTime | null
        employee?: Employee | null
        shift?: Shift | null
    }
): EmployeeShiftOffer => ({
    employeeId: params.employeeId,
    shiftId: params.shiftId,
    status: params.status,
    checkedInAt: params.checkedInAt ?? null,
    checkInLat: params.checkInLat ?? null,
    checkInLng: params.checkInLng ?? null,
    checkInNote: params.checkInNote ?? null,
    checkedOutAt: params.checkedOutAt ?? null,
    checkOutLat: params.checkOutLat ?? null,
    checkOutLng: params.checkOutLng ?? null,
    checkOutNote: params.checkOutNote ?? null,
    queueOrder: params.queueOrder ?? undefined,
    queueProcessOn: params.queueProcessOn ?? undefined,
    duration: params.checkedInAt && params.checkedOutAt ? params.checkedOutAt.diff(params.checkedInAt, 'hours') : null,
    employee: params.employee ?? null,
    shift: params.shift ?? null,
})

export const employeeShiftOfferFromDto = (dto: EmployeeShiftOfferDto): EmployeeShiftOffer => (createEmployeeShiftOffer({
    employeeId: dto.employee_id as EmployeeID,
    shiftId: dto.shift_id as ShiftID,
    status: dto.status,
    checkedInAt: dto.checked_in_at ? DateTime.fromISO(dto.checked_in_at) : null,
    checkInLat: dto.check_in_lat ?? null,
    checkInLng: dto.check_in_lng ?? null,
    checkInNote: dto.check_in_note ?? null,
    checkedOutAt: dto.checked_out_at ? DateTime.fromISO(dto.checked_out_at) : null,
    checkOutLat: dto.check_out_lat ?? null,
    checkOutLng: dto.check_out_lng ?? null,
    checkOutNote: dto.check_out_note ?? null,
    queueOrder: dto.queue_order ?? null,
    queueProcessOn: dto.queue_process_on ? DateTime.fromISO(dto.queue_process_on) : null,
    employee: dto.employee?.data ? employeeFromDto(dto.employee.data) : null,
    shift: dto.shift ? shiftFromDto(dto.shift.data) : null,
}))